import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import {
  CARRIERS,
  CARRIER_LABELS,
  Carrier,
  DELIVERY_METHOD_LABELS,
  LABEL_ISSUE_LABELS,
  LineInput,
  PRODUCTION_STATE_LABELS,
  PackingLineType,
  PackingOrderType,
  ShipmentType,
} from "types/ShippingTypes";
import {
  apiAssignShipmentLines,
  apiBookShipment,
  apiGetLabel,
  apiGetPackingOrder,
  apiGetPackingSlip,
  apiHandedToDriver,
  apiReadyForPickup,
} from "util/network/Shipping";
import ParcelsForm, {
  ParcelDraft,
  emptyParcel,
  parcelsToInput,
} from "./ParcelsForm";
import ShipmentsTable from "./ShipmentsTable";
import {
  AppLink,
  SeverityChip,
  ShipmentPage,
  carrierName,
  downloadLabel,
  errorText,
  formatKr,
  printPackingSlip,
} from "./common";

type Mode = "carrier" | "own_delivery" | "pickup";

const SHIPPABLE = ["PROCESSING", "SHIPPING"];

/** Quantity to pack per order line, as typed. */
type Picks = Record<number, { checked: boolean; quantity: string }>;

const PackingOrder: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const history = useHistory();

  const [order, setOrder] = useState<PackingOrderType>();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [picks, setPicks] = useState<Picks>({});
  const [mode, setMode] = useState<Mode>("carrier");
  const [carrier, setCarrier] = useState<Carrier>("gls");
  const [parcels, setParcels] = useState<ParcelDraft[]>([emptyParcel()]);
  const [justDone, setJustDone] = useState<ShipmentType | null>(null);

  const startForm = (data: PackingOrderType) => {
    const next: Picks = {};
    data.lines.forEach((line) => {
      if (line.remaining > 0) {
        // Lines still in production start unchecked.
        next[line.orderItemId] = {
          checked: line.production !== "in_production",
          quantity: String(line.remaining),
        };
      }
    });
    setPicks(next);
    setParcels([emptyParcel()]);
    setCarrier(data.defaultCarrier);
    setMode(
      data.deliveryMethod === "pickup"
        ? "pickup"
        : data.deliveryMethod === "own_delivery"
        ? "own_delivery"
        : "carrier"
    );
  };

  const load = (resetForm: boolean) => {
    apiGetPackingOrder(token, Number(orderId))
      .then((data) => {
        setOrder(data);
        if (resetForm) startForm(data);
      })
      .catch((error) => addToast({ type: "error", message: errorText(error) }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (token) load(true);
  }, [token, orderId]);

  const remainingLines = useMemo(
    () => order?.lines.filter((line) => line.remaining > 0) ?? [],
    [order]
  );

  const selected: LineInput[] = remainingLines
    .filter((line) => picks[line.orderItemId]?.checked)
    .map((line) => ({
      orderItemId: line.orderItemId,
      quantity: Number(picks[line.orderItemId].quantity.replace(",", ".")),
    }));
  const quantitiesValid = selected.every((line) => {
    const max = remainingLines.find(
      (l) => l.orderItemId === line.orderItemId
    )?.remaining;
    return (
      Number.isFinite(line.quantity) &&
      line.quantity > 0 &&
      max !== undefined &&
      line.quantity <= max
    );
  });
  // Lines still in production are listed last, as a later packing job.
  const packNowLines = remainingLines.filter(
    (line) => line.production !== "in_production"
  );
  const laterLines = remainingLines.filter(
    (line) => line.production === "in_production"
  );
  const pickedInProduction = remainingLines.filter(
    (line) =>
      line.production === "in_production" && picks[line.orderItemId]?.checked
  );

  const lineRow = (line: PackingLineType, later = false) => {
    const pick = picks[line.orderItemId] ?? {
      checked: false,
      quantity: String(line.remaining),
    };
    const quantity = Number(pick.quantity.replace(",", "."));
    const invalid =
      pick.checked &&
      (!Number.isFinite(quantity) ||
        quantity <= 0 ||
        quantity > line.remaining);
    // Total only for a usable quantity above one.
    const showTotal =
      Number.isFinite(quantity) && quantity > 1 && quantity <= line.remaining;
    return (
      <TableRow
        key={line.orderItemId}
        // A line still in production has light grey text.
        sx={
          later
            ? { "& .MuiTableCell-root": { color: "text.disabled" } }
            : undefined
        }
      >
        <TableCell padding="checkbox">
          <Checkbox
            checked={pick.checked}
            onChange={(event) =>
              setPicks({
                ...picks,
                [line.orderItemId]: {
                  ...pick,
                  checked: event.target.checked,
                },
              })
            }
          />
        </TableCell>
        <TableCell>{line.name}</TableCell>
        <TableCell>
          <Tooltip
            title={
              line.production === "ready"
                ? "The last QC step is done"
                : line.production === "in_production"
                ? "The last QC step is not done yet"
                : "No production for this line; check the part is here"
            }
          >
            <Chip
              size="small"
              color={
                line.production === "ready"
                  ? "success"
                  : line.production === "in_production"
                  ? "warning"
                  : "default"
              }
              label={PRODUCTION_STATE_LABELS[line.production]}
            />
          </Tooltip>
        </TableCell>
        <TableCell>
          {line.unitWeightKg > 0
            ? `${line.unitWeightKg.toFixed(2)} kg each${
                showTotal
                  ? ` · ${(line.unitWeightKg * quantity).toFixed(2)} kg`
                  : ""
              }`
            : "—"}
        </TableCell>
        <TableCell>{line.ordered}</TableCell>
        <TableCell>{line.shipped || ""}</TableCell>
        <TableCell>
          <TextField
            size="small"
            value={pick.quantity}
            disabled={!pick.checked}
            error={invalid}
            helperText={invalid ? `1-${line.remaining}` : undefined}
            onChange={(event) =>
              setPicks({
                ...picks,
                [line.orderItemId]: {
                  ...pick,
                  quantity: event.target.value,
                },
              })
            }
            inputProps={{ inputMode: "decimal" }}
            sx={{ width: 100 }}
          />{" "}
          of {line.remaining}
        </TableCell>
      </TableRow>
    );
  };

  const isSplit =
    selected.length < remainingLines.length ||
    selected.some(
      (line) =>
        line.quantity <
        (remainingLines.find((l) => l.orderItemId === line.orderItemId)
          ?.remaining ?? 0)
    );

  // Same sum as the backend: unit weight x quantity, plus the box.
  const estimateGrams = order
    ? Math.round(
        selected.reduce((sum, line) => {
          const unit =
            order.lines.find((l) => l.orderItemId === line.orderItemId)
              ?.unitWeightKg ?? 0;
          return (
            sum + unit * (Number.isFinite(line.quantity) ? line.quantity : 0)
          );
        }, 0) * 1000
      ) + order.defaultBoxWeightGrams
    : null;

  const paidToKg =
    order && !order.isCustomShippingMethod ? order.shippingBandToKg : null;

  const done = (shipment: ShipmentType, message: string) => {
    addToast({ type: "success", message });
    setJustDone(shipment);
    // The remaining quantities and the order status are worked out on the
    // server, so the one order is read again.
    load(true);
  };

  const fail = (error: unknown) =>
    addToast({ type: "error", message: errorText(error), keep: true });

  const submit = () => {
    if (!order || selected.length === 0 || !quantitiesValid) return;
    setBusy(true);
    let action: Promise<ShipmentType>;
    let message: string;
    if (mode === "pickup") {
      action = apiReadyForPickup(token, order.orderId, selected);
      message = "Ready for pickup; the customer gets an email";
    } else if (mode === "own_delivery") {
      action = apiHandedToDriver(token, order.orderId, selected);
      message = "Handed to driver; the customer gets an email";
    } else {
      const parcelInput = parcelsToInput(parcels);
      if (!parcelInput) {
        setBusy(false);
        return;
      }
      action = apiBookShipment(token, order.orderId, {
        lines: selected,
        parcels: parcelInput,
        carrier,
      });
      message = order.printEnabled
        ? "Label booked and sent to the label printer"
        : "Label booked; download it below (printing is off)";
    }
    action
      .then((shipment) => done(shipment, message))
      .catch(fail)
      .finally(() => setBusy(false));
  };

  const assignLines = (shipment: ShipmentType) => {
    if (!order || selected.length === 0 || !quantitiesValid) return;
    setBusy(true);
    apiAssignShipmentLines(token, shipment.id, selected)
      .then((updated) => done(updated, "Lines assigned to the label"))
      .catch(fail)
      .finally(() => setBusy(false));
  };

  const printSlip = (shipment: ShipmentType) =>
    apiGetPackingSlip(token, shipment.id)
      .then((slip) => {
        if (!printPackingSlip(slip)) {
          addToast({
            type: "warning",
            message: "The browser blocked the print window; allow pop-ups.",
          });
        }
      })
      .catch(fail);

  const download = (shipment: ShipmentType) =>
    apiGetLabel(token, shipment.id)
      .then((label) =>
        downloadLabel(
          label.base64,
          label.fileFormat,
          `label-${shipment.orderNo}-${shipment.id}`
        )
      )
      .catch(fail);

  if (loading) {
    return (
      <ShipmentPage title="Packing">
        <CircularProgress />
      </ShipmentPage>
    );
  }
  if (!order) {
    return (
      <ShipmentPage title="Packing">
        <Typography>Order not found.</Typography>
      </ShipmentPage>
    );
  }

  const shippable = SHIPPABLE.includes(order.status);
  const unassigned = order.shipments.filter(
    (s) => s.lines.length === 0 && s.state !== "cancelled"
  );
  const parcelsReady = mode !== "carrier" || parcelsToInput(parcels) !== null;
  const canSubmit =
    shippable &&
    !busy &&
    selected.length > 0 &&
    quantitiesValid &&
    parcelsReady;
  const address = order.shippingAddress;
  const openIssues = order.issues.filter((issue) => issue.state === "open");

  return (
    <ShipmentPage
      title={`Packing - order ${order.orderNo}`}
      actions={
        <Button
          startIcon={<ArrowBackIcon />}
          sx={{ color: "#fff" }}
          onClick={() => history.push("/shipment/packing")}
        >
          Queue
        </Button>
      }
    >
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 280 }}>
          <Typography variant="subtitle2">Ship to</Typography>
          <Typography variant="body2">
            {address?.company && (
              <>
                {address.company}
                <br />
              </>
            )}
            {[address?.firstName, address?.lastName].filter(Boolean).join(" ")}
            <br />
            {address?.street}
            <br />
            {address?.postalCode} {address?.city}, {address?.countryCode}
            <br />
            {address?.phone}
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 280 }}>
          <Typography variant="subtitle2">Order</Typography>
          <Typography variant="body2">
            <AppLink to={`/ordrestyring/${order.orderId}`}>
              {order.orderNo}
            </AppLink>{" "}
            · {order.status}
            <br />
            {order.customer}
            {order.poNumber && (
              <>
                <br />
                PO: {order.poNumber}
              </>
            )}
            <br />
            {DELIVERY_METHOD_LABELS[order.deliveryMethod]}
            {order.isCustomShippingMethod
              ? ` (custom: ${order.shippingMethod})`
              : ""}
            {" · paid "}
            {formatKr(order.shippingExVat)} ex VAT
            {paidToKg != null && (
              <>
                {" "}
                ({order.shippingBandFromKg}-{paidToKg} kg
                {order.shippingPriceColumn
                  ? `, ${carrierName(order.shippingPriceColumn)}`
                  : ""}
                )
              </>
            )}
          </Typography>
        </Paper>
      </Box>

      {openIssues.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {openIssues.map((issue) => (
            <Box
              key={issue.id}
              sx={{ display: "flex", gap: 1, alignItems: "center" }}
            >
              <SeverityChip severity={issue.severity} />
              <span>
                {LABEL_ISSUE_LABELS[issue.type] ?? issue.type}
                {issue.detail ? `: ${issue.detail}` : ""}
              </span>
            </Box>
          ))}
          <AppLink to="/shipment/label-issues">Label issues</AppLink>
        </Alert>
      )}

      {justDone && justDone.state !== "cancelled" && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setJustDone(null)}
          action={
            <Box sx={{ display: "flex", gap: 1 }}>
              {justDone.lines.length > 0 && (
                <Button size="small" onClick={() => printSlip(justDone)}>
                  Print packing slip
                </Button>
              )}
              {justDone.kind === "carrier" && justDone.hasLabel && (
                <Button size="small" onClick={() => download(justDone)}>
                  Download label
                </Button>
              )}
            </Box>
          }
        >
          {justDone.kind === "carrier"
            ? `${carrierName(justDone.carrier)} label ${
                justDone.trackingNo ?? ""
              } booked.`
            : justDone.kind === "pickup"
            ? "Ready for pickup."
            : "Handed to driver."}
        </Alert>
      )}

      {!shippable && remainingLines.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          The order is {order.status}; it can be packed once it is PROCESSING.
        </Alert>
      )}

      {remainingLines.length === 0 ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          Every line is in a shipment.
        </Alert>
      ) : (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Pack
          </Typography>
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Item</TableCell>
                <TableCell>Production</TableCell>
                <TableCell>Est. weight</TableCell>
                <TableCell>Ordered</TableCell>
                <TableCell>Already shipped</TableCell>
                <TableCell>Pack now</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {packNowLines.map((line) => lineRow(line))}
              {laterLines.length > 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ bgcolor: "action.hover" }}>
                    <Typography variant="body2">
                      <strong>Not ready for packing:</strong> still in
                      production. These lines stay on the order and are packed
                      in a later shipment.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {laterLines.map((line) => lineRow(line, true))}
            </TableBody>
          </Table>
          {pickedInProduction.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Still in production:{" "}
              {pickedInProduction.map((line) => line.name).join(", ")}. Check
              the parts are here and through QC.
            </Alert>
          )}
          {isSplit && selected.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Split: the rest stays in the packing queue.
            </Alert>
          )}

          {unassigned.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {unassigned.map((shipment) => (
                <Box
                  key={shipment.id}
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mb: 0.5,
                  }}
                >
                  <span>
                    {carrierName(shipment.carrier)} label{" "}
                    {shipment.trackingNo ?? shipment.shipmondoId} was booked in
                    Shipmondo; assign the lines it carries.
                  </span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={busy || selected.length === 0 || !quantitiesValid}
                    onClick={() => assignLines(shipment)}
                  >
                    Assign checked lines
                  </Button>
                </Box>
              ))}
            </Alert>
          )}

          {order.deliveryMethod === "pickup" ? (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Pickup order: put the parts on the pickup shelf and mark them
              ready. The customer gets the "ready for pickup" email.
            </Typography>
          ) : (
            <RadioGroup
              row
              value={mode}
              onChange={(event) => setMode(event.target.value as Mode)}
              sx={{ mb: 1 }}
            >
              <FormControlLabel
                value="carrier"
                control={<Radio />}
                label="Book a carrier label"
              />
              <FormControlLabel
                value="own_delivery"
                control={<Radio />}
                label="Own delivery (handed to driver)"
              />
            </RadioGroup>
          )}

          {mode === "carrier" && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}
            >
              <FormControl size="small" sx={{ width: 220 }}>
                <InputLabel>Carrier</InputLabel>
                <Select
                  label="Carrier"
                  value={carrier}
                  onChange={(event) =>
                    setCarrier(event.target.value as Carrier)
                  }
                >
                  {CARRIERS.map((value) => (
                    <MenuItem key={value} value={value}>
                      {CARRIER_LABELS[value]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <ParcelsForm
                parcels={parcels}
                onChange={setParcels}
                estimateGrams={selected.length ? estimateGrams : null}
                paidToKg={paidToKg}
                disabled={busy}
              />
            </Box>
          )}

          <Button variant="contained" disabled={!canSubmit} onClick={submit}>
            {busy && (
              <CircularProgress size={18} sx={{ mr: 1, color: "#fff" }} />
            )}
            {mode === "pickup"
              ? "Ready for pickup"
              : mode === "own_delivery"
              ? "Handed to driver"
              : "Book label"}
          </Button>
        </Paper>
      )}

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Shipments
      </Typography>
      <ShipmentsTable
        shipments={order.shipments}
        onChanged={() => load(true)}
        onRebooked={(_, created) => {
          setJustDone(created);
          load(true);
        }}
        paidToKg={paidToKg}
        estimateGrams={order.estimateGrams}
      />
    </ShipmentPage>
  );
};

export default PackingOrder;
